/**
 * What the Subscription card shows as the events receiver URL.
 *
 * `registered` is the URL the integration-service actually holds for the
 * subscription's hook (null when there is no live hook). `expected` is the URL
 * the backend would register today, from its own receiver config (null when it
 * has none). The card shows the registered URL whenever one exists, because that
 * is where events really go: showing only the expected URL is what hid a hook
 * registered against another environment's backend.
 *
 * The comparison uses the backend's expected URL, never one derived from this
 * app's API base: the browser may reach the API through a proxy or another host
 * than the one the integration-service calls back.
 */
export type ReceiverUrlView = {
  /** The URL to display. */
  shown: string
  /** True when a live hook points somewhere other than the expected URL. */
  mismatch: boolean
}

export function receiverUrlView(input: {
  registered: string | null | undefined
  expected: string | null | undefined
  /** Shown only when neither URL is known (no subscription yet). */
  fallback: string
}): ReceiverUrlView {
  const { registered, expected, fallback } = input
  if (!registered) return { shown: expected ?? fallback, mismatch: false }
  return { shown: registered, mismatch: !!expected && normalize(registered) !== normalize(expected) }
}

/**
 * A best-guess receiver URL for a tenant that has no subscription yet, from this
 * app's API base. `apiBase` may be relative (`/api`), so it is resolved against
 * `origin`.
 */
export function fallbackReceiverUrl(apiBase: string, origin: string, tenantId: string): string {
  const base = new URL(apiBase, origin).toString().replace(/\/+$/, '')
  return `${base}/integrations/webhooks/${tenantId}/events`
}

function normalize(url: string): string {
  return url.trim().replace(/\/+$/, '').toLowerCase()
}
